import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, Circle } from "lucide-react";
import { WORKSHOPS, type Workshop } from "@/content/workshops";
import { useNavigation, type ActivePanel } from "@/stores/useNavigation";
import { useProgress, resolveStatus } from "@/stores/useProgress";
import {
  WORKSHOP_TO_BUBBLE,
  useBubbleWorkshopLink,
} from "@/stores/useBubbleWorkshopLink";
import { cn } from "@/lib/utils";

/**
 * Bottom WS1-WS8 step bar. Ported from `#mc-bottom-nav` in
 * `_legacy/mc.html` (lines 1887-2120) and the `.ws-step` styles in
 * `_legacy/xaes.css` lines 388-471 (also ported into `src/index.css`).
 *
 * Each cell maps to one of the eight workshops; click jumps the
 * Mission Control panel switcher to the workshop's MC_WS{n} panel,
 * the "back arrow" CLOSE on each panel routes back to MC_00. Keyboard
 * shortcuts: 1-8 select a workshop, Escape returns to MC_00.
 *
 * Status styling follows the vanilla cascade via the `.ws-done`,
 * `.ws-active`, `.ws-locked` modifier classes on each `.ws-step`:
 *   - `done`     true-blue badge + check icon, true-blue label
 *   - `active`   sky badge with subtle pulse + sky label
 *   - `locked`   gold badge + gold label (vanilla treats these as
 *                browseable in the demo)
 *
 * The "selected" affordance (clicked but not active) adds `.ws-selected`
 * which mirrors the `.ws-active` background wash without overriding the
 * badge/label colours — same behaviour as the vanilla `ws-selected` JS.
 */

/**
 * Returns the `WS{n}` id parsed out of a `/workshop/:id` pathname, or
 * `null` when the route isn't a workshop dashboard. We don't use
 * `useParams` here because `WorkshopNavigator` is mounted in `AppLayout`,
 * which is the parent of the `/workshop/:id` route — so the params
 * context for `:id` is empty from this component's perspective.
 */
function parseWorkshopRouteId(pathname: string): Workshop["id"] | null {
  if (!pathname.startsWith("/workshop/")) return null;
  const rest = pathname.slice("/workshop/".length);
  const segment = rest.split("/")[0];
  const match = WORKSHOPS.find((w) => w.id === segment);
  return match ? match.id : null;
}

/**
 * Activity sub-route segment after `/workshop/:id/`, e.g. `"handshake"`
 * or `"stress-test"` — `null` on the plain dashboard route. Switching
 * workshops from inside an activity board keeps you in the same
 * activity (jump WS1's Stress-Test straight to WS2's Stress-Test)
 * instead of dropping back to the target workshop's dashboard.
 */
function parseActivitySegment(pathname: string): string | null {
  if (!pathname.startsWith("/workshop/")) return null;
  const rest = pathname.slice("/workshop/".length);
  const segment = rest.split("/")[1];
  return segment ?? null;
}

/**
 * Map a workshop's status to its `.ws-*` modifier class. Vanilla used
 * the same class names directly in markup; we centralise the lookup so
 * the modifier is the single source of truth.
 */
function statusClass(status: Workshop["status"]): string {
  switch (status) {
    case "done":
      return "ws-done";
    case "active":
      return "ws-active";
    case "locked":
      return "ws-locked";
  }
}

/**
 * Per-accent CSS-variable lookup. Used to seed `--accent-peach` and
 * `--accent-peach-rgb` inline on each `.ws-step` so the badge / num /
 * title cascade picks the workshop's own colour instead of falling
 * back to the status-driven defaults. Mirrors the inline
 * `style="--accent-peach:var(--accent-…)"` overrides at
 * `_legacy/mc.html` lines 1934-2074.
 */
const ACCENT_VAR: Record<Workshop["accent"], string> = {
  "true-blue": "var(--accent-true-blue)",
  sky: "var(--accent-sky)",
  gold: "var(--accent-gold)",
  melon: "var(--accent-melon)",
  peach: "var(--accent-peach)",
};

const ACCENT_RGB_VAR: Record<Workshop["accent"], string> = {
  "true-blue": "var(--accent-true-blue-rgb)",
  sky: "var(--accent-sky-rgb)",
  gold: "var(--accent-gold-rgb)",
  melon: "var(--accent-melon-rgb)",
  peach: "var(--accent-peach-rgb)",
};

function StepCard({
  workshop,
  status,
  isSelected,
  isMc,
  highlighted,
  onSelect,
  onHoverChange,
}: {
  workshop: Workshop;
  /** Effective status (facilitator override ?? static default). */
  status: Workshop["status"];
  isSelected: boolean;
  /** Render the MC CRT-monitor chrome (legacy `_legacy/xaes-nav.js`
   *  lines 1105-1117 inject this behind each MC bottom-nav step). */
  isMc: boolean;
  /** Lit up when this workshop's linked MC_00 capability bubble is
   *  hovered — see `useBubbleWorkshopLink`. */
  highlighted: boolean;
  onSelect: () => void;
  /** Fired on hover enter/leave so the MC_00 ring can highlight this
   *  workshop's linked capability bubble. */
  onHoverChange: (hovering: boolean) => void;
}) {
  const isActive = status === "active";

  // Per-workshop accent override. Legacy set `--accent-peach` inline
  // on every `.ws-step` (`_legacy/mc.html` line 1934 etc.) so the
  // badge / num / title cascade picked up the workshop's colour
  // rather than the status's default. We carry the same mechanism:
  // WS1/WS2 → true-blue, WS3/WS4 → sky, WS5–WS7 → gold, WS8 → melon.
  // The matching `--accent-peach-rgb` lets the active-pulse glow
  // tint correctly too. React requires `as React.CSSProperties` to
  // accept the `--foo` keys.
  const accentStyle = {
    "--accent-peach": ACCENT_VAR[workshop.accent],
    "--accent-peach-rgb": ACCENT_RGB_VAR[workshop.accent],
  } as React.CSSProperties;

  return (
    <button
      type="button"
      id={isMc ? `${workshop.id.toLowerCase()}-btn` : undefined}
      onClick={onSelect}
      className={cn(
        "ws-step",
        statusClass(status),
        isSelected && "ws-selected",
        highlighted && "ws-linked-highlight",
      )}
      aria-label={workshop.longTitle}
      aria-pressed={isSelected}
      style={accentStyle}
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
    >
      {/* CRT monitor shell — only on MC route. The screen div + chin bar
          ports the legacy `injectMonitor` IIFE. The container's own
          z-index sits at 0 so badge/num/title (z-index:2 via the CSS
          rule `#mc-bottom-nav .ws-step > :not(.ws-monitor)`) overlay it. */}
      {isMc && (
        <div className="ws-monitor" aria-hidden>
          <div className="ws-monitor-screen" />
          <div className="ws-monitor-chin">
            <div className="ws-monitor-chin-bar" />
          </div>
        </div>
      )}
      <div
        className={cn(
          "ws-step-badge",
          isActive && !isSelected &&
            "animate-[ws-step-pulse_2.4s_ease-in-out_infinite]",
        )}
      >
        {/* Badge glyph. The badge itself is an outlined ring (the CSS
            border on `.ws-step-badge`); the icon inside is the inner glyph:
              - DONE      → a tick (the completion signal facilitators look
                            for).
              - SELECTED  → a FILLED inner circle, so the workshop you're
                            previewing reads as "in focus": ring + solid dot.
              - otherwise → an outlined inner circle — i.e. two concentric
                            outlined rings, the resting state for every
                            workshop that's neither finished nor selected.
                            The in-flight (ACTIVE) one is set apart by the
                            subtle badge pulse below, not by a third dot. */}
        {status === "done" ? (
          <Check className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
        ) : isSelected ? (
          <Circle
            className="h-3 w-3"
            fill="currentColor"
            strokeWidth={2}
            aria-hidden
          />
        ) : (
          <Circle className="h-3 w-3" strokeWidth={2} aria-hidden />
        )}
      </div>
      {/* Legacy treats WS8 as the "PREPARE" phase — the navigator badge
          and the chat picker both render the longer "PREPARE" label
          instead of "WS8" (see `_legacy/mc.html` line 2074). All other
          cells stay as `WS{n}`. Phone-tier cells are ~44-48px wide;
          "PREPARE" has no space to break on, so the browser's own
          overflow-wrap split it mid-word at an ugly point ("PREPA/RE").
          `ws-step-num-short` is a deliberate, pre-chosen break — CSS
          swaps the two by width, same pattern as `.ws-step-title`. */}
      <div className="ws-step-num">
        {workshop.num === 8 ? (
          <>
            <span className="ws-step-num-full">PREPARE</span>
            <span className="ws-step-num-short">PREP</span>
          </>
        ) : (
          `WS${workshop.num}`
        )}
      </div>
      <div className="ws-step-title" title={workshop.longTitle}>
        {workshop.title}
      </div>
    </button>
  );
}

export function WorkshopNavigator() {
  const activePanel = useNavigation((s) => s.activePanel);
  const setActivePanel = useNavigation((s) => s.setActivePanel);
  const workshopStatusOverrides = useProgress((s) => s.workshopStatus);
  const hoveredBubbleId = useBubbleWorkshopLink((s) => s.hoveredBubbleId);
  const setHoveredWorkshop = useBubbleWorkshopLink((s) => s.setHoveredWorkshop);
  const location = useLocation();
  const navigate = useNavigate();

  const workshopRouteId = parseWorkshopRouteId(location.pathname);
  const isWorkshopRoute = workshopRouteId !== null;
  const activitySegment = parseActivitySegment(location.pathname);
  const targetPath = (id: Workshop["id"]): string =>
    activitySegment ? `/workshop/${id}/${activitySegment}` : `/workshop/${id}`;

  // On `/workshop/:id`, the URL is the source of truth for the active
  // card. On `/` (or anywhere else) we defer to the in-page MC panel.
  const currentPanel: ActivePanel | null = isWorkshopRoute
    ? (`MC_${workshopRouteId}` as ActivePanel)
    : activePanel;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Don't hijack typing inside inputs / textareas.
      const target = e.target as HTMLElement | null;
      if (target?.tagName === "INPUT" || target?.tagName === "TEXTAREA") return;

      // A Radix layer (dialog / select / popover) that dismisses on Escape
      // calls preventDefault() before closing — that Escape was "spent" on
      // the layer, not on leaving the workshop. And while a modal dialog is
      // mounted (Radix sets body pointer-events: none), the 1-8 workshop
      // jumps must not fire underneath it either.
      if (e.defaultPrevented) return;
      if (document.body.style.pointerEvents === "none") return;

      if (e.key === "Escape") {
        if (isWorkshopRoute) {
          e.preventDefault();
          setActivePanel("MC_00");
          navigate("/");
          return;
        }
        if (activePanel !== "OPENING" && activePanel !== "MC_00") {
          e.preventDefault();
          setActivePanel("MC_00");
        }
        return;
      }
      const numKey = Number(e.key);
      if (Number.isInteger(numKey) && numKey >= 1 && numKey <= 8) {
        const ws = WORKSHOPS.find((w) => w.num === numKey);
        if (ws) {
          e.preventDefault();
          if (isWorkshopRoute) {
            navigate(targetPath(ws.id), { replace: true });
          } else {
            setActivePanel(ws.panel);
          }
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activePanel, setActivePanel, isWorkshopRoute, navigate, activitySegment]);

  if (activePanel === "OPENING" && !isWorkshopRoute) return null;

  // MC route gets the `#mc-bottom-nav` id so the CRT-monitor + glow-orb
  // CSS overrides in `src/index.css` only apply on Mission Control.
  const isMc = !isWorkshopRoute;

  return (
    <nav
      id={isMc ? "mc-bottom-nav" : undefined}
      aria-label="Workshop navigator"
      className="ws-step-bar flex-shrink-0"
    >
      {WORKSHOPS.map((w) => (
        <StepCard
          key={w.id}
          workshop={w}
          status={resolveStatus(workshopStatusOverrides, w.id, w.status)}
          isSelected={currentPanel === w.panel}
          isMc={isMc}
          highlighted={
            hoveredBubbleId !== null && WORKSHOP_TO_BUBBLE[w.id] === hoveredBubbleId
          }
          onHoverChange={(hovering) => setHoveredWorkshop(hovering ? w.id : null)}
          onSelect={() => {
            if (isWorkshopRoute) {
              navigate(targetPath(w.id), { replace: true });
            } else {
              setActivePanel(w.panel);
            }
          }}
        />
      ))}
    </nav>
  );
}
